import { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

import useCreateVisitMutation from "../../hooks/useCreateVisitMutation";
import { showApiErrorToast } from "../../utils/helpers";

const MAX_NAME_LENGTH = 255;

const toWholeNumber = (value) => (value === "" ? 0 : Number(value));

const isWholeNumberInRange = (value, min, max = Infinity) =>
  Number.isInteger(value) && value >= min && value <= max;

const CreateVisitDialog = ({ open, onClose, trialId, onCreated }) => {
  const [visitName, setVisitName] = useState("");
  const [startDays, setStartDays] = useState("");
  const [startHours, setStartHours] = useState("");
  const [endDays, setEndDays] = useState("");
  const [endHours, setEndHours] = useState("");

  const createVisitMutation = useCreateVisitMutation();

  useEffect(() => {
    if (open) {
      setVisitName("");
      setStartDays("");
      setStartHours("");
      setEndDays("");
      setEndHours("");
    }
  }, [open]);

  const name = visitName.trim();
  const startDaysValue = toWholeNumber(startDays);
  const startHoursValue = toWholeNumber(startHours);
  const endDaysValue = toWholeNumber(endDays);
  const endHoursValue = toWholeNumber(endHours);

  const startDaysError = !isWholeNumberInRange(startDaysValue, 0);
  const startHoursError = !isWholeNumberInRange(startHoursValue, 0, 23);
  const endDaysError = !isWholeNumberInRange(endDaysValue, 0);
  const endHoursError = !isWholeNumberInRange(endHoursValue, 0, 23);

  const visitSchedule = startDaysValue * 24 + startHoursValue;
  const endVisitSchedule = endDaysValue * 24 + endHoursValue;
  const durationError =
    !endDaysError && !endHoursError && (endDays !== "" || endHours !== "") && endVisitSchedule <= 0;

  const isValid =
    name.length > 0 &&
    !startDaysError &&
    !startHoursError &&
    !endDaysError &&
    !endHoursError &&
    endVisitSchedule > 0;

  const handleSubmit = async () => {
    try {
      const result = await createVisitMutation.mutateAsync({
        trialId,
        visitName: name,
        visitSchedule,
        endVisitSchedule,
      });
      toast.success("Visit created successfully.");
      onCreated?.(result);
      onClose();
    } catch (error) {
      showApiErrorToast(error, "Failed to create visit.");
    }
  };

  const isSubmitting = createVisitMutation.isPending;

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Create Visit</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            The visit number follows the schedule: visits are ordered by when they start.
          </Typography>

          <TextField
            label="Visit name"
            size="small"
            value={visitName}
            onChange={(e) => setVisitName(e.target.value)}
            inputProps={{ maxLength: MAX_NAME_LENGTH, "data-testid": "create-visit-name-input" }}
            disabled={isSubmitting}
            autoFocus
          />

          <Typography variant="subtitle2">Visit start</Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Start days"
              type="number"
              size="small"
              value={startDays}
              onChange={(e) => setStartDays(e.target.value)}
              inputProps={{ min: 0, step: 1, "data-testid": "create-visit-start-days-input" }}
              error={startDaysError}
              helperText={startDaysError ? "Whole number, 0 or more" : ""}
              disabled={isSubmitting}
            />
            <TextField
              label="Start hours"
              type="number"
              size="small"
              value={startHours}
              onChange={(e) => setStartHours(e.target.value)}
              inputProps={{
                min: 0,
                max: 23,
                step: 1,
                "data-testid": "create-visit-start-hours-input",
              }}
              error={startHoursError}
              helperText={startHoursError ? "Whole number, 0 to 23" : ""}
              disabled={isSubmitting}
            />
          </Stack>

          <Typography variant="subtitle2">Visit end (duration)</Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label="End days"
              type="number"
              size="small"
              value={endDays}
              onChange={(e) => setEndDays(e.target.value)}
              inputProps={{ min: 0, step: 1, "data-testid": "create-visit-end-days-input" }}
              error={endDaysError || durationError}
              helperText={endDaysError ? "Whole number, 0 or more" : ""}
              disabled={isSubmitting}
            />
            <TextField
              label="End hours"
              type="number"
              size="small"
              value={endHours}
              onChange={(e) => setEndHours(e.target.value)}
              inputProps={{
                min: 0,
                max: 23,
                step: 1,
                "data-testid": "create-visit-end-hours-input",
              }}
              error={endHoursError || durationError}
              helperText={endHoursError ? "Whole number, 0 to 23" : ""}
              disabled={isSubmitting}
            />
          </Stack>
          {durationError && (
            <Typography variant="body2" color="error">
              Duration must be greater than zero.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          data-testid="create-visit-submit-btn"
          disabled={!isValid || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreateVisitDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  trialId: PropTypes.number,
  onCreated: PropTypes.func,
};

export default CreateVisitDialog;
